/**
 * ComparisonCards — v2.4.0
 *
 * Cards de comparaison période courante vs précédente sur la page Rapports.
 * Sélecteur de période (semaine / mois / trimestre / année).
 * Pour chaque métrique : valeur actuelle + delta% avec arrow + valeur N-1.
 *
 * 6 métriques retournées par `/api/analytics/comparison` :
 *   - new_leads
 *   - won_deals
 *   - lost_deals
 *   - conversion_rate (%)
 *   - revenue (FCFA)
 *   - avg_cycle_days
 */
import { useEffect, useState, useMemo } from "react";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Users,
  Trophy,
  XCircle,
  Percent,
  Wallet,
  Clock,
} from "lucide-react";
import { analyticsApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";

const PERIODS = [
  { value: "week", label: "Semaine" },
  { value: "month", label: "Mois" },
  { value: "quarter", label: "Trimestre" },
  { value: "year", label: "Année" },
];

const METRIC_DEFS = [
  {
    key: "new_leads",
    label: "Nouveaux leads",
    icon: Users,
    color: "text-blue-600",
    formatter: (v) => Math.round(v).toLocaleString("fr-FR"),
    higherIsBetter: true,
  },
  {
    key: "won_deals",
    label: "Ventes conclues",
    icon: Trophy,
    color: "text-emerald-600",
    formatter: (v) => Math.round(v).toLocaleString("fr-FR"),
    higherIsBetter: true,
  },
  {
    key: "conversion_rate",
    label: "Taux de closing",
    icon: Percent,
    color: "text-amber-600",
    formatter: (v) => `${v.toFixed(1)} %`,
    higherIsBetter: true,
  },
  {
    key: "revenue",
    label: "Chiffre d'affaires",
    icon: Wallet,
    color: "text-violet-600",
    formatter: (v) => {
      // Compact FCFA — au-dessus de 1M afficher en M
      if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} M FCFA`;
      if (v >= 1_000) return `${(v / 1_000).toFixed(0)} k FCFA`;
      return `${Math.round(v)} FCFA`;
    },
    higherIsBetter: true,
  },
  {
    key: "lost_deals",
    label: "Ventes perdues",
    icon: XCircle,
    color: "text-red-500",
    formatter: (v) => Math.round(v).toLocaleString("fr-FR"),
    higherIsBetter: false,
  },
  {
    key: "avg_cycle_days",
    label: "Cycle moyen",
    icon: Clock,
    color: "text-zinc-600",
    formatter: (v) => `${v.toFixed(1)} j`,
    higherIsBetter: false,
  },
];

export default function ComparisonCards() {
  const { activeProjectId } = useSettings();
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    analyticsApi
      .comparison({ projet_id: activeProjectId, period })
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeProjectId, period]);

  const periodLabel = useMemo(() => {
    const p = PERIODS.find((x) => x.value === period);
    return p?.label || period;
  }, [period]);

  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading text-base font-bold">
            Comparaison période
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {periodLabel} en cours vs {periodLabel.toLowerCase()} précédent
            {activeProjectId ? " · projet actif" : " · tous projets"}
          </p>
        </div>
        <div className="inline-flex rounded-md border border-border/60 bg-background p-0.5">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPeriod(p.value)}
              className={`rounded px-2.5 py-1 text-xs font-semibold transition-colors ${
                period === p.value
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`comparison-period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid gap-3 md:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-lg bg-muted/40"
            />
          ))}
        </div>
      ) : !data ? (
        <p className="text-sm text-muted-foreground">
          Comparaison indisponible pour cette période.
        </p>
      ) : (
        <div className="grid gap-3 md:grid-cols-3">
          {METRIC_DEFS.map((def) => {
            const m = data.metrics?.[def.key];
            if (!m) return null;
            return <MetricCard key={def.key} def={def} metric={m} />;
          })}
        </div>
      )}
    </div>
  );
}

function MetricCard({ def, metric }) {
  const { current, previous, delta_pct } = metric;
  const Icon = def.icon;
  // Direction de la flèche selon delta_pct ET la sémantique de la métrique.
  // Pour "lost_deals" et "avg_cycle_days", une baisse = positif.
  let arrow = Minus;
  let arrowColor = "text-muted-foreground";
  if (delta_pct != null && delta_pct !== 0) {
    const isUp = delta_pct > 0;
    const isGood =
      (def.higherIsBetter && isUp) || (!def.higherIsBetter && !isUp);
    arrow = isUp ? TrendingUp : TrendingDown;
    arrowColor = isGood ? "text-emerald-600" : "text-red-500";
  }
  const ArrowIcon = arrow;
  return (
    <div
      className="rounded-lg border border-border/60 bg-background p-3"
      data-testid={`metric-${def.key}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className={`h-4 w-4 ${def.color}`} />
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {def.label}
          </span>
        </div>
        {delta_pct != null && (
          <span
            className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
              arrowColor === "text-emerald-600"
                ? "bg-emerald-50 dark:bg-emerald-950/30"
                : arrowColor === "text-red-500"
                  ? "bg-red-50 dark:bg-red-950/30"
                  : "bg-muted"
            } ${arrowColor}`}
          >
            <ArrowIcon className="h-2.5 w-2.5" />
            {delta_pct > 0 ? "+" : ""}
            {delta_pct.toFixed(1)} %
          </span>
        )}
      </div>
      <div className="mt-2 font-heading text-2xl font-bold">
        {def.formatter(current || 0)}
      </div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">
        Précédent : {def.formatter(previous || 0)}
      </div>
    </div>
  );
}
